import { matchPath, useLocation, useSearchParams } from 'react-router-dom';
import { useAppSelector } from 'redux/store';
import { linkTargetsAnotherAccount } from 'helpers/accountIdentity';
import { RoutesPaths } from 'routes/Routes.types';

// The single definition of "an invited sign-in is pending for an account other
// than the one in session". Two consumers read it and must not drift apart: the
// route guard, which stops redirecting into the outgoing unit, and the login
// form, which stops submitting the link's credentials without a gesture.
//
// It answers from two sources on purpose, and neither alone is enough:
//
//  - `fromLink`, the URL, because a restored session makes the route guard
//    redirect on the very first commit, before anything can be recorded. Scoped
//    to the login route: password reset and email verification are public routes
//    too and carry their own `username` param.
//  - `pending`, the recorded value, because both halves of the URL reading are
//    perishable — the login form deletes `username`/`pass` on mount, and
//    `amplifyUser` is cleared by any `currentAuthUserAmplify` rejection, which on
//    a live page can land within a second of load.
//
// `fromLink` is what PublicRoutes records, in the first commit, while the session
// is still whole. `pending` is the answer the redirect guard reads afterwards.
//
// `pending` is scoped to the routes the invited sign-in actually spans, which is
// /login AND /new-password: an invitation's temporary password always answers
// NEW_PASSWORD_REQUIRED, so the switch is only half done when the visitor leaves
// /login. Scoping it to /login alone released the guard on that navigation while
// userSlice.user was still the OUTGOING tenant, so PublicRoutes read isLoggedIn
// as true and redirected into the unit the link was supposed to move them out of.
// Scoping by route rather than by Login's unmount also makes a recorded value
// inert everywhere else, so a hard navigation away cannot leave it set for the
// life of the tab.
//
// `outgoing` is the same answer for the other side of the switch: who the visitor
// is being moved OUT of. It is read from the recorded value rather than from
// `amplifyUser` for the reason above — the confirmation prompt is answered at
// human pace, and by then the live reading may be gone. It is what names the
// account in the prompt and what keys its cached Cognito tokens for removal.
const useInvitedAccountLink = (): {
  fromLink: string | null;
  outgoingFromSession: string | null;
  pending: string | null;
  outgoing: string | null;
  staleOffRoute: boolean;
} => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const amplifyUser = useAppSelector(state => state.authSlice.amplifyUser);
  const recorded = useAppSelector(state => state.authSlice.invitedUsername);
  const recordedOutgoing = useAppSelector(
    state => state.authSlice.outgoingUsername,
  );

  const invitedUsername = searchParams.get('username');
  // matchPath with end:true rather than a pathname comparison, because
  // react-router 6.3 also serves these routes with a trailing slash.
  const onRoute = (path: RoutesPaths) =>
    !!matchPath({ path: `/${path}`, end: true }, location.pathname);
  // The URL reading stays scoped to /login: /reset-password and /verify-by-email
  // are public routes too and carry their own `username` param.
  const onLoginRoute = onRoute(RoutesPaths.LOGIN);
  const onInvitedRoute = onLoginRoute || onRoute(RoutesPaths.NEW_PASSWORD);
  const fromLink =
    onLoginRoute && linkTargetsAnotherAccount(invitedUsername, amplifyUser)
      ? invitedUsername
      : null;

  return {
    fromLink,
    // Paired with `fromLink` so the recorder cannot capture one without the
    // other: null whenever there is nothing to record.
    outgoingFromSession: fromLink ? amplifyUser?.username ?? null : null,
    pending: onInvitedRoute ? fromLink ?? recorded : null,
    outgoing: onInvitedRoute ? recordedOutgoing : null,
    staleOffRoute: !onInvitedRoute && !!recorded,
  };
};

export default useInvitedAccountLink;
